const LAST_PAGE_KEY = 'studierai_last_visited_page';

// Pagine che non devono essere salvate come ultima pagina visitata
const EXCLUDED_PAGES = [
  '/',
  '/signIn',
  '/signUp',
  '/verifica-email',
  '/controlla-verifica-email',
  '/reimposta-password',
  '/risultato-esame',
  '/payment-completed',
  '/payment-success',
  '/welcome'
];

// Pattern di pagine dinamiche che devono essere escluse
const EXCLUDED_PAGE_PATTERNS = [
  /^\/home\/ripeti(\/.*)?$/, // Esclude /home/ripeti e /home/ripeti/qualsiasi-cosa
  /^\/chat\/.*$/, // Esclude tutte le pagine /chat/documentID
  /^\/risultato-esame(\/.*)?$/ // Esclude /risultato-esame e /risultato-esame/qualsiasi-cosa
];

// Pagine che richiedono autenticazione
const PROTECTED_PAGES = [
  '/home',
  '/chat',
  '/risultato-esame',
  // `/scatta/:id` e' l'indirizzo che il telefono apre dal QR del Centro Fonti.
  // Dichiararlo protetto serve al RITORNO: chi arriva senza sessione fa il
  // login e deve ritrovarsi esattamente li', non sulla home generica. Senza
  // questa riga funzionerebbe lo stesso per caso (la pagina non e' esclusa),
  // ma resterebbe una coincidenza invece di un contratto.
  '/scatta'
];


/**
 * Verifica se un URL è valido per essere salvato come ultima pagina
 * @param {string} pathname - Il pathname della route
 * @returns {boolean} - True se l'URL può essere salvato
 */
export const isValidPageToSave = (pathname) => {
  // Non salvare le pagine escluse
  if (EXCLUDED_PAGES.includes(pathname)) {
    return false;
  }
  
  // Non salvare pagine che iniziano con i path esclusi
  if (EXCLUDED_PAGES.some(excluded => pathname.startsWith(excluded + '/'))) {
    return false;
  }
  
  // Non salvare pagine che matchano i pattern esclusi
  if (EXCLUDED_PAGE_PATTERNS.some(pattern => pattern.test(pathname))) {
    return false;
  }
  
  return true;
};

/**
 * Verifica se un URL richiede autenticazione
 * @param {string} pathname - Il pathname della route
 * @returns {boolean} - True se l'URL richiede autenticazione
 */
export const isProtectedPage = (pathname) => {
  return PROTECTED_PAGES.some(protectedPath => 
    pathname === protectedPath || pathname.startsWith(protectedPath + '/')
  );
};

/**
 * Salva l'ultima pagina visitata nel localStorage
 * @param {string} pathname - Il pathname della route da salvare
 * @param {string} search - I query parameters della route
 */
export const saveLastVisitedPage = (pathname, search = '') => {
  if (!isValidPageToSave(pathname)) {
    return;
  }
  
  const fullPath = pathname + search;
  
  try {
    localStorage.setItem(LAST_PAGE_KEY, fullPath);
  } catch (error) {
    console.warn('Impossibile salvare l\'ultima pagina visitata:', error);
  }
};

/**
 * Recupera l'ultima pagina visitata dal localStorage
 * @param {boolean} isAuthenticated - Se l'utente è autenticato
 * @returns {string|null} - L'URL dell'ultima pagina o null se non disponibile/valida
 */
export const getLastVisitedPage = (isAuthenticated = false) => {
  try {
    const lastPage = localStorage.getItem(LAST_PAGE_KEY);
    
    if (!lastPage) {
      return null;
    }
    
    // Se la pagina richiede autenticazione ma l'utente non è autenticato, ignora
    if (isProtectedPage(lastPage) && !isAuthenticated) {
      return null;
    }
    
    // Verifica che la pagina sia ancora valida per il redirect
    const pathname = lastPage.split('?')[0];
    if (!isValidPageToSave(pathname)) {
      return null;
    }
    
    return lastPage;
  } catch (error) {
    console.warn('Impossibile recuperare l\'ultima pagina visitata:', error);
    return null;
  }
};

/**
 * Pulisce l'ultima pagina visitata dal localStorage
 */
export const clearLastVisitedPage = () => {
  try {
    localStorage.removeItem(LAST_PAGE_KEY);
  } catch (error) {
    console.warn('Impossibile pulire l\'ultima pagina visitata:', error);
  }
};

/**
 * Ottiene la pagina di default per un utente autenticato
 * @returns {string} - L'URL della pagina di default
 */
export const getDefaultAuthenticatedPage = () => {
  return '/home';
};

/**
 * Ottiene la pagina di default per un utente non autenticato
 * @returns {string} - L'URL della pagina di default
 */
export const getDefaultUnauthenticatedPage = () => {
  return '/';
};
// ── Il link da cui si arriva senza sessione (STU-463) ───────────────────────
// Chi apre un link di StudierAI senza sessione (il pulsante di una mail, un
// promemoria, il link aperto nel browser da chi usa l'app iPhone) passa
// dall'accesso e deve tornare esattamente li'. L'ultima pagina visitata qui
// sopra non basta: esclude `/home/ripeti`, e Google e Apple portano sempre in
// home. Vale una volta sola e nella stessa scheda (sessionStorage), solo per
// pagine protette; le pagine `/chat` no, vivono dello stato della navigazione.
const DOPO_ACCESSO_KEY = 'studierai_dopo_accesso';

const linkDopoAccessoValido = (link) => typeof link === 'string'
  && link.startsWith('/') && !link.startsWith('//')
  && !link.startsWith('/chat')
  && isProtectedPage(link.split('?')[0]);

/**
 * Ricorda la pagina protetta chiesta senza sessione, per tornarci dopo l'accesso.
 * @param {string} pathname
 * @param {string} search
 */
export const ricordaDopoAccesso = (pathname, search = '') => {
  const link = `${pathname}${search || ''}`;
  if (!linkDopoAccessoValido(link)) return;
  try {
    sessionStorage.setItem(DOPO_ACCESSO_KEY, link);
  } catch (error) {
    // Senza storage, dopo l'accesso si va dove si andava prima.
  }
};

/**
 * Restituisce la pagina ricordata e la dimentica: si usa una volta, subito dopo l'accesso.
 * @returns {string|null}
 */
export const prendiDopoAccesso = () => {
  let link = null;
  try {
    link = sessionStorage.getItem(DOPO_ACCESSO_KEY);
    sessionStorage.removeItem(DOPO_ACCESSO_KEY);
  } catch (error) {
    return null;
  }
  return linkDopoAccessoValido(link) ? link : null;
};
